// Sammelleiste (bei Auswahl), Objekt-Summe und Rechtsklick-Menü der Rechnungsliste (design_handoff_rechnungen §4,
// Screens 06/07). Texte und Abläufe wie bisher in components/InvoicesView.tsx — hier nur Darstellung.
import type { ReactNode, Ref } from 'react';
import {
  Buildings, CaretDown, CircleNotch, DownloadSimple, Eye, GlobeSimple, Money, PaperPlaneTilt, Trash, User, UserGear, UsersThree, X,
} from '@phosphor-icons/react';
import { eur } from '../../../../lib/rechnungenAnsicht/darstellung';

const DUNKEL_KNOPF = {
  height: 38, padding: '0 13px', gap: 6, borderRadius: 13, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
  background: 'rgba(255,255,255,0.12)', color: '#fff',
} as const;

export interface SammelleisteProps {
  anzahl: number;
  nichtSichtbar: number;
  laeuft: boolean;
  fortschritt: string | null;
  fehler: string | null;
  onBezahlt: () => void;
  onHerunterladen: () => void;
  onMail: () => void;
  zustaendigRef: Ref<HTMLDivElement>;
  zustaendigOffen: boolean;
  onZustaendigUmschalten: () => void;
  mitglieder: readonly { id: string; full_name: string }[];
  teams: readonly { id: string; name: string }[];
  onZustaendig: (id: string | null, typ: 'mitarbeiter' | 'team' | null) => void;
  onAufheben: () => void;
  mobil: boolean;
}

export function Sammelleiste(p: SammelleisteProps) {
  const h = p.mobil ? 44 : 38;
  const knopf = { ...DUNKEL_KNOPF, height: h, opacity: p.laeuft ? 0.5 : 1, cursor: p.laeuft ? 'default' : 'pointer' };
  return (
    <div role="region" aria-label="Sammelaktionen" className="immo-pop-in sticky flex flex-wrap items-center"
      style={{ top: 10, zIndex: 25, gap: 6, padding: '8px 8px 8px 16px', borderRadius: 22, color: '#fff',
        background: 'linear-gradient(160deg, rgba(28,32,44,0.92), rgba(18,20,28,0.92))', WebkitBackdropFilter: 'blur(24px)', backdropFilter: 'blur(24px)',
        boxShadow: '0 20px 40px -18px rgba(0,0,0,0.5), inset 0 0 0 0.5px rgba(255,255,255,0.12)' }}>
      <span style={{ fontSize: 13, fontWeight: 800, marginRight: 6 }}>
        {p.anzahl} ausgewählt
        {p.nichtSichtbar > 0 && <span style={{ fontWeight: 600, opacity: 0.6 }}> · {p.nichtSichtbar} nicht sichtbar</span>}
      </span>
      <button type="button" onClick={p.onBezahlt} disabled={p.laeuft} data-tip="Als bezahlt markieren – alle gewählten Belege auf bezahlt setzen"
        className="inline-flex items-center" style={{ ...knopf, fontWeight: 800, background: '#34c759' }}>
        {p.laeuft && !p.fortschritt ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Money size={14} weight="bold" />}
        Als bezahlt markieren
      </button>
      <button type="button" onClick={p.onHerunterladen} disabled={p.laeuft} data-tip="Herunterladen – PDFs der gewählten Belege nacheinander laden"
        className="inline-flex items-center" style={knopf}>
        {p.fortschritt ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <DownloadSimple size={14} weight="bold" />}
        {p.fortschritt ? `Lädt ${p.fortschritt}` : 'Herunterladen'}
      </button>
      <button type="button" onClick={p.onMail} disabled={p.laeuft} data-tip="Per Mail senden – PDFs als Anhang in einer E-Mail (Pille)"
        className="inline-flex items-center" style={knopf}>
        <PaperPlaneTilt size={14} weight="bold" />Per Mail senden
      </button>
      <div className="relative" ref={p.zustaendigRef}>
        <button type="button" onClick={p.onZustaendigUmschalten} disabled={p.laeuft} aria-expanded={p.zustaendigOffen}
          data-tip="Zuständigkeit setzen – für alle gewählten Belege" className="inline-flex items-center" style={knopf}>
          <UserGear size={14} weight="bold" />Zuständigkeit setzen<CaretDown size={11} weight="bold" style={{ transform: p.zustaendigOffen ? 'rotate(180deg)' : 'none' }} />
        </button>
        {p.zustaendigOffen && (
          <div role="menu" aria-label="Zuständigkeit setzen" className="immo-pop-in immo-scroll absolute flex flex-col"
            style={{ left: 0, top: h + 6, width: 240, maxHeight: 320, overflowY: 'auto', zIndex: 40, borderRadius: 18, padding: 6, gap: 2,
              background: 'var(--sheet)', color: 'var(--ink)', boxShadow: '0 24px 50px -16px var(--shade), inset 0 0 0 1px var(--line)' }}>
            <MenueKnopf onClick={() => p.onZustaendig(null, null)}><GlobeSimple size={14} weight="bold" color="var(--acc)" />Global</MenueKnopf>
            {p.mitglieder.map((m) => <MenueKnopf key={m.id} onClick={() => p.onZustaendig(m.id, 'mitarbeiter')}><User size={14} weight="bold" color="var(--acc)" />{m.full_name}</MenueKnopf>)}
            {p.teams.map((t) => <MenueKnopf key={t.id} onClick={() => p.onZustaendig(t.id, 'team')}><UsersThree size={14} weight="bold" color="var(--acc)" />{t.name}</MenueKnopf>)}
          </div>
        )}
      </div>
      <button type="button" onClick={p.onAufheben} className="inline-flex items-center" style={{ ...DUNKEL_KNOPF, height: h, background: 'transparent', color: 'rgba(255,255,255,0.75)' }}>
        <X size={13} weight="bold" />Auswahl aufheben
      </button>
      {p.fehler && <span role="alert" style={{ width: '100%', padding: '2px 0 4px', fontSize: 12, fontWeight: 700, color: '#ff8a80' }}>{p.fehler}</span>}
    </div>
  );
}

function MenueKnopf({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" role="menuitem" onClick={onClick} className="immo-zeile-hover flex items-center text-left"
      style={{ gap: 8, minHeight: 38, padding: '9px 11px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: 'var(--ink)', background: 'transparent' }}>
      {children}
    </button>
  );
}

export function ObjektSumme({ name, anzahl, brutto, offen }: { name: string; anzahl: number; brutto: number; offen: number }) {
  return (
    <div className="flex flex-wrap items-center" style={{ gap: 14, padding: '12px 16px', borderRadius: 18, background: 'var(--acc-soft)', fontSize: 13, fontWeight: 700 }}>
      <span className="flex min-w-0 items-center" style={{ gap: 7, fontWeight: 800 }}>
        <Buildings size={16} weight="fill" color="var(--acc)" className="shrink-0" /><span className="truncate">{name}</span>
      </span>
      <span style={{ color: 'var(--ink2)' }}>{anzahl} {anzahl === 1 ? 'Beleg' : 'Belege'}</span>
      <span className="tabular-nums" style={{ fontWeight: 800 }}>{eur(brutto)} gesamt</span>
      <span className="tabular-nums" style={{ color: 'var(--warn)' }}>davon offen {eur(offen)}</span>
    </div>
  );
}

export function Kontextmenue({ x, y, onOeffnen, onLoeschen }: { x: number; y: number; onOeffnen: () => void; onLoeschen: () => void }) {
  // Nicht über den Fensterrand hinaus
  const links = Math.min(x, (typeof window !== 'undefined' ? window.innerWidth : x + 200) - 196);
  const oben = Math.min(y, (typeof window !== 'undefined' ? window.innerHeight : y + 120) - 110);
  const zeile = { gap: 10, minHeight: 40, padding: '9px 12px', borderRadius: 11, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, background: 'transparent' } as const;
  return (
    <div role="menu" aria-label="Beleg" className="immo-pop-in fixed flex flex-col" onClick={(e) => e.stopPropagation()}
      style={{ top: oben, left: links, zIndex: 200, minWidth: 188, padding: 6, gap: 2, borderRadius: 16, background: 'var(--sheet)', color: 'var(--ink)',
        boxShadow: '0 24px 50px -16px var(--shade), inset 0 0 0 1px var(--line)' }}>
      <button type="button" role="menuitem" onClick={onOeffnen} className="immo-zeile-hover flex items-center text-left" style={{ ...zeile, color: 'var(--ink)' }}>
        <Eye size={15} weight="bold" color="var(--ink2)" />Öffnen
      </button>
      <button type="button" role="menuitem" onClick={onLoeschen} className="immo-zeile-hover flex items-center text-left" style={{ ...zeile, color: 'var(--bad)' }}>
        <Trash size={15} weight="bold" />Löschen
      </button>
    </div>
  );
}
